/** Stable gap codes shared by source import, authoring notes and aggregate telemetry. */
export const FORM_IMPORT_ISSUE_CODES = [
  'unsupported_question_type', 'file_upload_not_imported', 'media_not_imported',
  'branching_not_imported', 'shuffle_not_imported', 'quiz_scoring_not_imported',
  'email_verification_not_imported', 'multi_answer_grid_not_imported',
  'matrix_settings_not_imported', 'date_without_year_not_imported',
  'duration_not_imported', 'rating_presentation_changed', 'rating_labels_not_imported',
  'answer_recall_not_imported', 'choice_settings_not_imported',
  'invalid_question', 'step_limit', 'source_item_unreadable',
] as const;

/** A known difference between the source and its editable imported draft. */
export type FormImportIssueCode = typeof FORM_IMPORT_ISSUE_CODES[number];

/** Safe, authoring-only explanation for one source item or an aggregate omission. */
export interface FormImportIssue {
  code: FormImportIssueCode;
  action: 'skipped' | 'changed' | 'omitted';
  message: string;
  /** One-based source item position, when the issue belongs to a specific item. */
  sourceIndex?: number;
  label?: string;
  /** Number of affected items, defaulting to one. */
  count?: number;
}

/** Import outcome kept with the creator's draft, never with respondent questions. */
export interface FormImportReport {
  provider: 'google' | 'microsoft' | 'typeform';
  importedCount: number;
  skippedCount: number;
  issues: FormImportIssue[];
}

/** Recognize a transport object without accepting arrays or null. */
function record(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** Bound counters before they reach rendering or draft storage. */
function boundedInteger(value: unknown, minimum: number, maximum: number): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= minimum && value <= maximum;
}

/** Keep bounded plain text, allowing authored line breaks but no control characters. */
function boundedText(value: unknown, maximum: number): value is string {
  return typeof value === 'string' && value.length <= maximum
    && !/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/.test(value);
}

/** Validate and copy only the safe report contract at SSE and draft boundaries. */
export function parseFormImportReport(value: unknown): FormImportReport | null {
  if (!record(value) || Object.keys(value).some(key => !['provider', 'importedCount', 'skippedCount', 'issues'].includes(key))
    || (value.provider !== 'google' && value.provider !== 'microsoft' && value.provider !== 'typeform')
    || !boundedInteger(value.importedCount, 0, 200) || !boundedInteger(value.skippedCount, 0, 1000000)
    || !Array.isArray(value.issues) || value.issues.length > 1000) return null;
  const issues: FormImportIssue[] = [];
  for (const issue of value.issues) {
    if (!record(issue) || Object.keys(issue).some(key => !['code', 'action', 'message', 'sourceIndex', 'label', 'count'].includes(key))
      || !(FORM_IMPORT_ISSUE_CODES as readonly unknown[]).includes(issue.code)
      || (issue.action !== 'skipped' && issue.action !== 'changed' && issue.action !== 'omitted')
      || !boundedText(issue.message, 500) || !issue.message.trim()
      || (issue.sourceIndex !== undefined && !boundedInteger(issue.sourceIndex, 1, 1000000))
      || (issue.label !== undefined && !boundedText(issue.label, 1000))
      || (issue.count !== undefined && !boundedInteger(issue.count, 1, 1000000))) return null;
    issues.push({ code: issue.code as FormImportIssueCode, action: issue.action, message: issue.message,
      ...(issue.sourceIndex !== undefined ? { sourceIndex: issue.sourceIndex as number } : {}),
      ...(issue.label !== undefined ? { label: issue.label as string } : {}), count: (issue.count as number | undefined) ?? 1 });
  }
  return { provider: value.provider, importedCount: value.importedCount, skippedCount: value.skippedCount, issues };
}
